import type React from "react"
import { useEffect, useRef } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { useTenant } from "../../contexts/TenantContext"
import { PageLoader } from "../ui/page-loader"

interface AuthGuardProps {
  children: React.ReactNode
}

/**
 * AuthGuard protects routes by redirecting unauthenticated users to login.
 * Also kicks off a non-blocking `fetchAccessibleCustomers` so CustomerSwitcher
 * populates after refresh on any protected route (`/account`, `/admin`,
 * customer tree, picker). Customer selection for `/` is handled by RootRedirect.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, fetchAccessibleCustomers } = useAuth()
  const { tenant } = useTenant()
  const location = useLocation()
  const fetchedTenantRef = useRef<string | null>(null)

  // Non-blocking: do not gate render on the customer list.
  useEffect(() => {
    if (!isAuthenticated || !tenant?.id) return
    if (fetchedTenantRef.current === tenant.id) return
    fetchedTenantRef.current = tenant.id
    fetchAccessibleCustomers(tenant.id).catch((err) => {
      console.error("Failed to fetch customers:", err)
    })
  }, [isAuthenticated, tenant?.id, fetchAccessibleCustomers])

  if (isLoading) {
    return <PageLoader className="min-h-screen" />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}
